/** Shared colour and surface contract for every Recharts producer. */

export const LIGHT_CHART_COLORS = [
  '#1D6FD8', '#C9780C', '#15803D', '#DC2626',
  '#7C3AED', '#DB2777', '#0D9488', '#475569',
];

export const DARK_CHART_COLORS = [
  '#6BA8F5', '#F4C95C', '#7ED063', '#F0786A',
  '#B08EE0', '#F59A82', '#5EDDD4', '#B8B8B8',
];

export const CHART_GRID_COLOR = 'var(--color-border)';
export const CHART_AXIS_COLOR = 'var(--color-text-muted)';
export const CHART_TOOLTIP_STYLE = {
  backgroundColor: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md, 8px)',
  color: 'var(--color-text-primary)',
  boxShadow: 'var(--shadow-card)',
};

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

export function normalizeCustomColors(colors) {
  if (!Array.isArray(colors)) return [];
  return colors
    .map((color) => String(color || '').trim().toUpperCase())
    .filter((color, index, list) => HEX_COLOR.test(color) && list.indexOf(color) === index)
    .slice(0, 8);
}

export function getActiveChartTheme() {
  if (typeof document === 'undefined') return 'light';
  const themedRoot = document.querySelector('.layout[data-theme], [data-theme]');
  const theme = themedRoot?.getAttribute('data-theme');
  if (theme === 'dark' || theme === 'light') return theme;
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    const preference = window.matchMedia('(prefers-color-scheme: dark)');
    return preference?.matches ? 'dark' : 'light';
  }
  return 'light';
}

/** Resolve the UI-only "auto" option before generating a static server image. */
export function resolveChartOptions(options = {}) {
  const requestedTheme = options.theme || 'auto';
  return {
    ...options,
    theme: requestedTheme === 'auto' ? getActiveChartTheme() : requestedTheme,
    requested_theme: requestedTheme,
    custom_colors: normalizeCustomColors(options.custom_colors),
  };
}

function customColorForTheme(color) {
  return `light-dark(${color}, color-mix(in srgb, ${color} 68%, white))`;
}

/**
 * Theme-aware series colour. CSS tokens intentionally remain unresolved so an
 * already-mounted SVG reacts when its nearest [data-theme] scope changes.
 */
export function getDataColor(index = 0, customColors = []) {
  const i = Math.abs(Number(index) || 0);
  const normalized = normalizeCustomColors(customColors);
  if (normalized.length) return customColorForTheme(normalized[i % normalized.length]);

  const paletteIndex = i % LIGHT_CHART_COLORS.length;
  return `var(--data-color-${paletteIndex + 1}, ${LIGHT_CHART_COLORS[paletteIndex]})`;
}

export function getDataColorList(count, customColors = []) {
  const n = Math.max(0, Math.min(count || 0, 32));
  return Array.from({ length: n }, (_, i) => getDataColor(i, customColors));
}
